import { MutedAlertSites, MutedAlertCameras } from "./alertsInterfaces";

/**
 * Muting hides alerts only from the live (Unresolved / UNCLAIMED) queue.
 * Muted alerts are auto-resolved and retained in the Resolved queue — for
 * every user INCLUDING the one who muted (before this gate, the muter's own
 * Resolved view filtered them out and showed an empty list while other
 * users saw the rows — BT-1134 follow-up).
 */
export const hideMutedFromView = (
  filterType: string | undefined | null
): boolean => filterType === "UNCLAIMED";

/**
 * Trims a timestamp to the first 10 digits
 * @param timestamp - The timestamp to trim (can be string or number)
 * @returns The trimmed timestamp as a number
 */
const trimTimestamp = (
  timestamp: string | number | undefined | null
): number => {
  if (timestamp === undefined || timestamp === null) {
    return 0;
  }
  const timestampStr = timestamp.toString();
  // Extract first 10 digits from the timestamp string
  const firstTenDigits = timestampStr.substring(0, 10);
  return parseInt(firstTenDigits, 10);
};

/**
 * Checks if an alert should be filtered out based on muted sites and cameras
 * @param alert - The alert object to check
 * @param mutedAlertSites - Array of muted site objects
 * @param mutedAlertCameras - Array of muted camera objects
 * @returns true if the alert should be filtered out (muted), false otherwise
 */
export const isAlertMuted = (
  alert: any,
  mutedAlertSites: MutedAlertSites,
  mutedAlertCameras: MutedAlertCameras
): boolean => {
  if (!alert) {
    return false;
  }

  // Server truth (ENG-1309): the connector stamps dispatch_muted on the
  // alert record when an operational mute suppressed delivery. This is
  // authoritative for the alert itself; the muted lists below are the shared
  // fleet-wide mute state (ENG-2103) and also cover not-yet-stamped alerts.
  if (alert.dispatch_muted === true) {
    return true;
  }

  // Early return if alert is missing required properties
  if (!alert.approx_capture_timestamp) {
    return false;
  }

  const alertTimestamp = trimTimestamp(alert.approx_capture_timestamp);

  // Check if alert is muted by site
  const isMutedBySite = mutedAlertSites.some((mutedSite) => {
    const trimmedStartTime = trimTimestamp(mutedSite.mute_start_time);
    const trimmedEndTime = trimTimestamp(mutedSite.mute_end_time);

    return (
      alert.customer_pk === mutedSite.site_id &&
      alertTimestamp >= trimmedStartTime &&
      alertTimestamp <= trimmedEndTime
    );
  });

  // Check if alert is muted by camera
  const isMutedByCamera = mutedAlertCameras.some((mutedCamera) => {
    const trimmedStartTime = trimTimestamp(mutedCamera.mute_start_time);
    const trimmedEndTime = trimTimestamp(mutedCamera.mute_end_time);

    return (
      alert.camera_pk === mutedCamera.camera_id &&
      alertTimestamp >= trimmedStartTime &&
      alertTimestamp <= trimmedEndTime
    );
  });

  return isMutedBySite || isMutedByCamera;
};
